/**
 * Detect "extenso" fields paired with `brl_currency` source fields inside a form
 * schema section. A pairing is established when:
 *   - The field id ends with `_EXTENSO` and there is a sibling field whose id is the
 *     prefix and whose `mask === "brl_currency"`, OR
 *   - The field declares an explicit `extensoOf: "<sourceFieldId>"` pointing at a
 *     sibling `brl_currency` field (used when ids don't share the convention,
 *     e.g. VALOR_EXTENSO ↔ VALOR_TOTAL in proposta-compra-imovel).
 *
 * The utility returns a Map<extensoFieldId, sourceFieldId> for any list of fields
 * (works for plain section fields, repeatable section fields, and the per-option
 * field list of a conditional section).
 */

const EXTENSO_SUFFIX_RE = /_EXTENSO$/i;

export function isExtensoFieldId(fieldId) {
  return typeof fieldId === "string" && EXTENSO_SUFFIX_RE.test(fieldId);
}

/**
 * @param {Array<{id:string, mask?:string, extensoOf?:string}>} fields
 * @returns {Map<string, string>} extensoFieldId -> sourceFieldId
 */
export function detectExtensoPairsInFields(fields) {
  const result = new Map();
  if (!Array.isArray(fields)) return result;

  const brlSourceIds = new Set(
    fields.filter((f) => f && f.mask === "brl_currency").map((f) => f.id)
  );

  for (const field of fields) {
    if (!field || typeof field.id !== "string") continue;

    if (typeof field.extensoOf === "string" && brlSourceIds.has(field.extensoOf)) {
      result.set(field.id, field.extensoOf);
      continue;
    }

    if (!isExtensoFieldId(field.id)) continue;
    const candidate = field.id.replace(EXTENSO_SUFFIX_RE, "");
    if (brlSourceIds.has(candidate)) {
      result.set(field.id, candidate);
    }
  }

  return result;
}
